import { useTranslation } from 'react-i18next'
import { generatePath, Link } from 'react-router'
import { Swiper, SwiperSlide } from 'swiper/react'

import { BlogPost, TextMaterialCategory } from '@entities/Blog'

import { URL_BLOG_POST } from '@shared/contants'
import ArticleCard from '@shared/ui/aritcle-card'
import Button from '@shared/ui/button'

import BlogEmpty from './empty'

import styles from './styles.module.scss'

type BlogListProps = {
  currentCategorySlug?: string;
  showCategories?: boolean;
  categories?: TextMaterialCategory[];
  articles: BlogPost[];
  onCategoryChange: (category?: TextMaterialCategory) => void;
};

/**
 * Паттерн раскладки блога:
 * - Секция 1: 1 большая (2 строки слева) + 2 маленьких справа (3 статьи)
 * - Секция 2: 4 маленьких в ряд
 * - Секция 3: 4 маленьких в ряд  
 * - Секция 4: 1 большая (2 строки слева) + 2 маленьких справа (3 статьи)
 * - И так далее...
 */
function getArticleLayout (index: number): { isBig: boolean; sectionType: 'hero' | 'grid' } {
  // Паттерн повторяется каждые 11 статей: 3 + 4 + 4 = 11
  const cycleIndex = index % 14 // 3 + 4 + 4 + 3 = 14
  
  // Первая секция hero (0, 1, 2) - индекс 0 большой
  if (cycleIndex < 3) {
    return { isBig: cycleIndex === 0, sectionType: 'hero' }
  }
  
  // Первый ряд маленьких (3, 4, 5, 6)
  if (cycleIndex < 7) {
    return { isBig: false, sectionType: 'grid' }
  }
  
  // Второй ряд маленьких (7, 8, 9, 10)
  if (cycleIndex < 11) {
    return { isBig: false, sectionType: 'grid' }
  }
  
  // Вторая секция hero (11, 12, 13) - индекс 11 большой
  return { isBig: cycleIndex === 11, sectionType: 'hero' }
}

export default function BlogList ({
  currentCategorySlug,
  showCategories = true,
  categories = [],
  articles,
  onCategoryChange,
}: BlogListProps) {
  const { t: tb } = useTranslation('blog')

  if (!articles || articles.length === 0) {
    return <BlogEmpty />
  }

  // Группируем статьи по секциям
  const sections: { type: 'hero' | 'grid'; items: { article: BlogPost; isBig: boolean; index: number }[] }[] = []
  
  articles.forEach((article, index) => {
    const layout = getArticleLayout(index)
    const lastSection = sections[sections.length - 1]
    
    if (!lastSection || lastSection.type !== layout.sectionType) {
      sections.push({
        type: layout.sectionType,
        items: [{ article, isBig: layout.isBig, index }]
      })
    } else {
      lastSection.items.push({ article, isBig: layout.isBig, index })
    }
  })

  return (
    <div className={styles.root}>
      <div className={styles.root__top}>
        <h2 className={styles.root__title}>{tb('blog')}</h2>
        {showCategories && (
          <Swiper
            className={styles.root__slider}
            slidesPerView="auto"
            spaceBetween={8}
          >
            <SwiperSlide className={styles.root__slide}>
              <Button
                className={styles.root__buttonFilters}
                theme={currentCategorySlug ? 'grey' : 'primary'}
                onClick={() => onCategoryChange(undefined)}
              >
                {tb('categories.all')}
              </Button>
            </SwiperSlide>
            {categories.map((el, i) => (
              <SwiperSlide
                className={styles.root__slide}
                key={`${el?.title}-${i}`}
              >
                <Button
                  className={styles.root__buttonFilters}
                  theme={currentCategorySlug !== el?.slug ? 'grey' : 'primary'}
                  key={el?.slug}
                  onClick={() => onCategoryChange(el)}
                >
                  {el?.title}
                </Button>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>

      {sections.map((section, sectionIndex) => (
        <div 
          key={sectionIndex} 
          className={section.type === 'hero' ? styles.root__heroGrid : styles.root__smallGrid}
        >
          {section.items.map(({ article, isBig, index }) => {
            const postLink = generatePath(URL_BLOG_POST, {
              categorySlug: article?.category?.slug || '',
              slug: article?.slug || '',
            })

            return (
              <div 
                className={isBig ? styles.root__itemBig : styles.root__item} 
                key={article?.id || index}
              >
                <Link to={postLink}>
                  <ArticleCard isBigCard={isBig} data={article} />
                </Link>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
